import React, {useState} from "react"
import {useTranslation} from "react-i18next"
import {Row, Col, Spin} from "antd"
import {v4 as uuid} from "uuid"
import {BasePopup} from "components/popup"
import {Icon} from "components/Icon"
import {BaseFileInput} from "components/inputs"
import {BaseButton} from "components/buttons"
import {taskService} from "services"
import {getFileExtension, handleError, isAnswerProcessingError, toastError, toastSuccess} from "helpers"
import styles from "./ScanUploadTaskPopup.module.css"
import {useModel} from "hooks"
import {TaskStatus} from "types/tasks"
import {TaskProcessing} from "../TaskProcessing"
import {useTaskUserPolling} from "../../../hooks/useTaskUserPolling"
import {FILE_UPLOADING_ACCEPT_FOR} from "types/files"
import {get, isEmpty, keyBy, map} from "lodash"
import {uploadUsingPreSignedUrl} from "helpers/chat"

export function ScanUploadTaskPopup(props) {
    const model = useModel()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [files, setFiles] = useState<File[]>([])
    const [isProcessing, setIsProcessing] = useState(false)
    const [isAlreadyProcessing, setIsAlreadyProcessing] = useState(false)
    const {isShow, onClose, taskId, subTaskId, taskUserId, taskUserEncodedSecurityLevelWeb} = props
    const {t} = useTranslation(["tasks"])
    const {isChecked, isTimedOut, refresh} = useTaskUserPolling({
        taskId,
        subTaskId,
        taskUserId,
        enabled: isShow && isProcessing,
        onSettled: (status) => {
            setIsProcessing(false)
            setIsAlreadyProcessing(false)
            if ([TaskStatus.TODO, TaskStatus.OVERDUE].includes(status)) {
                toastError(t("tasks.processingFailed"))
                return
            }
            toastSuccess("Submit successful", "", {duration: 1})
            onClose(true)
        }
    })

    const onChangeData = (value) => {
        setFiles(value)
    }

    const onClickSubmit = async () => {
        if (isEmpty(files)) {
            toastError("Please select a file")
            return
        }
        try {
            setIsSubmitting(true)
            if (!taskUserId) {
                toastError("TaskUserId is required")
                setIsSubmitting(false)
                return
            }
            const newAcceptedFileInfos = files.map((file) => ({
                id: uuid(),
                extension: getFileExtension(file.name),
                file
            }))
            const filesExtensions = map(newAcceptedFileInfos, ({extension, id}) => ({fileExtension: extension, id}))
            const uploadAwsFilePayloads: Promise<any>[] = []
            const uploadedFiles = await taskService.createUploadLink({files: filesExtensions})
            const filesById = keyBy(newAcceptedFileInfos, "id")
            for (const item of uploadedFiles) {
                const file = get(filesById[item.id], "file")
                uploadAwsFilePayloads.push(uploadUsingPreSignedUrl(item.urlForUploading, file))
            }
            await Promise.all(uploadAwsFilePayloads)
            const fileUrls = uploadedFiles.map((item) => item.url)
            let params = `sl=${taskUserEncodedSecurityLevelWeb}`
            if (model.isSubmittingTaskFromEmail) {
                params = `sl=${model.securityLevelEncoded}`
            }
            await taskService.answerScanTask({taskUserId, urls: fileUrls}, params)
            // the answer can be processed asynchronously, the polling decides when it is finished
            setIsProcessing(true)
        } catch (error) {
            if (isAnswerProcessingError(error)) {
                setIsAlreadyProcessing(true)
                setIsProcessing(true)
            } else {
                handleError(error)
            }
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <BasePopup isShow={isShow} onClose={() => onClose(false)} leftIcon="UPLOAD_THIN" width="70vw">
            <div className={styles.wrap}>
                <p className={styles.title}>Scan/upload</p>
                {isProcessing && !isChecked && <Spin size="large" />}
                {isProcessing && isChecked && (
                    <TaskProcessing
                        isTimedOut={isTimedOut}
                        isAlreadyProcessing={isAlreadyProcessing}
                        onRefresh={refresh}
                    />
                )}
                <Row gutter={[40, 40]} style={isProcessing ? {display: "none"} : undefined}>
                    <Col span={24}>
                        <div className={styles.item}>
                            <BaseFileInput
                                onChange={onChangeData}
                                className={styles.fileNameWrap}
                                accept={FILE_UPLOADING_ACCEPT_FOR.DOCUMENT_AND_IMAGE}
                                multiple>
                                <span className={styles.iconWrap}>
                                    <Icon icon="UPLOAD_THIN" />
                                </span>
                            </BaseFileInput>
                            <span className={styles.text}>Upload</span>
                        </div>
                    </Col>
                </Row>
                <div className={styles.actionWrap} style={isProcessing ? {display: "none"} : undefined}>
                    <BaseButton title="Submit" onClick={onClickSubmit} loading={isSubmitting} />
                </div>
            </div>
        </BasePopup>
    )
}
